import { createAsyncThunk } from '@reduxjs/toolkit';
import variantsApi from '../../api/variantsApi';
import dealApi from '../../api/dealApi';
import optionsApi from '../../api/optionsApi';
import { IPriceDisplay } from '../../../common/model/IPlatformConfig';
import basketApi, { IBasketInitRequest } from '../../api/basketApi';
import IStockListRequestPayload from '../../../stock/model/IStockListRequestPayload';
import stockApi from '../../../stock/api/stockApi';
import IStockValidateRequestPayload from '../../../stock/model/validate/IStockValidateRequestPayload';
import IDimensions from '../../../common/model/IDimensions';

// Disable error serialization since error handling is happening on
// reducer level and we need axios response data for that.
// The warnings for unserializable error in state is ignored in
// `configureStore` middleware options.
const serializeError = (error: any) => error;

const thunkActions = {
  fetchVariantsList: createAsyncThunk(
    'variantsList/fetchVariantList',
    async (modelId: string) => {
      const response = await variantsApi.list(modelId);

      return response.data;
    },
    { serializeError },
  ),
  fetchVariantDetails: createAsyncThunk(
    'variantsDetails/fetchVariantDetails',
    async (carId: string) => {
      const response = await variantsApi.details(carId);

      return response.data;
    },
    { serializeError },
  ),
  fetchVariantSpecifications: createAsyncThunk(
    'variantSpecifications/fetchVariantSpecifications',
    async (variantId: string) => {
      const response = await variantsApi.specifications(variantId);

      return { variantId, specifications: response.data };
    },
    { serializeError },
  ),
  fetchOptions: createAsyncThunk(
    'options/fetchOptions',
    async ({ carId }: { carId: string }) => {
      return await optionsApi.list(carId);
    },
    { serializeError },
  ),
  initBasket: createAsyncThunk(
    'basket/init',
    // @TODO: We should find a better way for postcode and dealer id.
    async ({
      initRequest,
      priceDisplay,
      dimensions,
    }: {
      initRequest: IBasketInitRequest;
      priceDisplay: IPriceDisplay;
      dimensions: IDimensions;
    }) => {
      const response = await basketApi.init(initRequest, dimensions);

      return { deal: response.data, priceDisplay };
    },
    { serializeError },
  ),
  selectCar: createAsyncThunk(
    'basket/selectCar',
    async ({
      carId,
      configurationSourceUrl,
      priceDisplay,
    }: {
      carId: string;
      configurationSourceUrl: string;
      priceDisplay: IPriceDisplay;
    }) => {
      const response = await basketApi.selectCar(carId, configurationSourceUrl);

      return { deal: response.data, priceDisplay };
    },
    { serializeError },
  ),
  selectInterior: createAsyncThunk(
    'basket/selectInterior',
    async ({
      interior,
      configurationSourceUrl,
      priceDisplay,
    }: {
      interior: string;
      configurationSourceUrl: string;
      priceDisplay: IPriceDisplay;
    }) => {
      const response = await basketApi.selectInterior(interior, configurationSourceUrl);

      return { deal: response.data, priceDisplay };
    },
    { serializeError },
  ),
  selectExterior: createAsyncThunk(
    'basket/selectExterior',
    async ({
      exterior,
      configurationSourceUrl,
      priceDisplay,
    }: {
      exterior: string;
      configurationSourceUrl: string;
      priceDisplay: IPriceDisplay;
    }) => {
      const response = await basketApi.selectExterior(exterior, configurationSourceUrl);

      return { deal: response.data, priceDisplay };
    },
    { serializeError },
  ),
  approveCashDeal: createAsyncThunk(
    'deal/approveCashDeal',
    async ({ partExchangeId = null, priceDisplay }: { partExchangeId: string | null; priceDisplay: IPriceDisplay }) => {
      const response = await dealApi.approveCashDeal(partExchangeId);

      return { deal: response.data.activeDeal, priceDisplay };
    },
    { serializeError },
  ),
  fetchStockList: createAsyncThunk(
    'stockList/fetchList',
    async (payload: IStockListRequestPayload) => {
      const response = await stockApi.list(payload);

      return response.data;
    },
    { serializeError },
  ),

  selectStockCar: createAsyncThunk(
    'stock/selectCar',
    async ({ stockRequest, priceDisplay }: { stockRequest: IBasketInitRequest; priceDisplay: IPriceDisplay }) => {
      const response = await basketApi.selectStockCar(stockRequest);

      return { deal: response.data, priceDisplay };
    },
    { serializeError },
  ),

  validateSelections: createAsyncThunk(
    'stock/validateSelections',
    async ({ validationPayload }: { validationPayload: IStockValidateRequestPayload }) => {
      const response = await stockApi.validateSelections(validationPayload);

      return {
        data: response.data,
        nextVehicleId: validationPayload.nextVehicle.vehicleId,
      };
    },
    { serializeError },
  ),
};

export default thunkActions;
